import { createSelector } from 'reselect';
import cloneDeep from 'lodash.clonedeep';
import { toggleValueInArray } from 'app/dealer/utils';
import { ALL_SERVICE_NAMES } from './ServicesPanel/services/names/index.names';
import { ALL_CATEGORIES } from './ServicesPanel/services/index';

/**
 * *****************
 * @section
 * getters
 * *****************
 */
const getDealerProfileSettingsById = (dict, id) => dict[id];

export const getAllToggledProfileServicesForDealer = ({
  dealerId,
  branchProfileSettingsDictionary,
}) => {
  // copy profile settings
  const newBranchProfileSettingsDictionary = cloneDeep(
    branchProfileSettingsDictionary,
  );

  const currentDealerSettings = getDealerProfileSettingsById(
    newBranchProfileSettingsDictionary,
    dealerId,
  );

  const allSelectedServices = Object.values(currentDealerSettings).flat();
  // loop through categories and
  // if some services are selected,
  // else remove all selected services, else add all services
  ALL_CATEGORIES.forEach(category => {
    const allServicesForCategory = Object.values(ALL_SERVICE_NAMES[category]);

    if (allSelectedServices.length > 0) {
      newBranchProfileSettingsDictionary[dealerId][category] = [];
    } else {
      newBranchProfileSettingsDictionary[dealerId][
        category
      ] = allServicesForCategory;
    }
  });
  return newBranchProfileSettingsDictionary;
};

export const getUpdateOneProfileServiceForDealer = ({
  dealerId,
  serviceName,
  selectedDealerProfileSettingsCategory,
  branchProfileSettingsDictionary,
}) => {
  // copy profile settings
  const newBranchProfileSettingsDictionary = cloneDeep(
    branchProfileSettingsDictionary,
  );

  if (!newBranchProfileSettingsDictionary[dealerId]) {
    return newBranchProfileSettingsDictionary;
  }
  // get currently selected services
  const services =
    newBranchProfileSettingsDictionary[dealerId][
      selectedDealerProfileSettingsCategory
    ] || [];

  // toggle the the selected service on or off
  const updatedSevices = toggleValueInArray(services, serviceName);

  // update dictionary
  newBranchProfileSettingsDictionary[dealerId][
    selectedDealerProfileSettingsCategory
  ] = updatedSevices;

  return newBranchProfileSettingsDictionary;
};

/**
 *
 * @param {object} param0
 * @param {Record<string, object>} - dictionary of branches indexed by branch id
 *
 * @returns {Record<string, object>} - dictionary of dealer profile services indexed by branch id
 */
export const getBranchesWithProfileSettings = ({ branchesDictionary }) => {
  const newOptionsDictionary = {};
  const branchIds = Object.keys(branchesDictionary);

  branchIds.forEach(branchId => {
    // O(1) - SERVICE_CATEGORIES is constant (3 entries)
    ALL_CATEGORIES.forEach(category => {
      if (!newOptionsDictionary[branchId]) {
        newOptionsDictionary[branchId] = {};
      }
      const services = branchesDictionary[branchId][category];

      newOptionsDictionary[branchId][category] = services;
    });
  });

  return newOptionsDictionary;
};

/**
 * *****************
 * @section
 * state selectors
 * *****************
 */

/**
 * @param {Object} state
 * @returns {import('./reducer').DealerProfileSettingsState}
 */
const dealerProfileSettings = state => state.dealerProfileSettings;

/**
 * @const selectBranchProfileSettingsDictionary
 * @type {(state: Object) => Object}
 */
export const selectBranchProfileSettingsDictionary = createSelector(
  [dealerProfileSettings],
  ({ branchProfileSettingsDictionary = {} }) => branchProfileSettingsDictionary,
);

export const selectDealerIdBeingEdited = createSelector(
  [dealerProfileSettings],
  ({ dealerIdBeingEdited = '' }) => dealerIdBeingEdited,
);

export const selectDealerProfileSettingsApplyChangesToAllSelected = createSelector(
  [dealerProfileSettings],
  ({ dealerProfileSettingsApplyChangesToAllSelected }) =>
    dealerProfileSettingsApplyChangesToAllSelected,
);

export const selectDealerProfileSettingsSubmitInProgress = createSelector(
  [dealerProfileSettings],
  ({ dealerProfileSettingsSubmitInProgress }) =>
    dealerProfileSettingsSubmitInProgress,
);
export const selectDealerProfileSettingsSubmitError = createSelector(
  [dealerProfileSettings],
  ({ dealerProfileSettingsSubmitError }) => dealerProfileSettingsSubmitError,
);

export const selectDealerProfileSettingsSubmitDisabled = createSelector(
  [dealerProfileSettings],
  ({ dealerProfileSettingsSubmitDisabled }) =>
    dealerProfileSettingsSubmitDisabled,
);
export const selectDealerProfileSettingsSubmittedSuccessfully = createSelector(
  [dealerProfileSettings],
  ({ dealerProfileSettingsSubmittedSuccessfully }) =>
    dealerProfileSettingsSubmittedSuccessfully,
);

export const selectDealerProfileSettingsFormHasBeenEdited = createSelector(
  [dealerProfileSettings],
  ({ dealerProfileSettingsFormHasBeenEdited }) =>
    dealerProfileSettingsFormHasBeenEdited,
);

export const selectSaveChangesModalIsVisible = createSelector(
  [dealerProfileSettings],
  ({ saveChangesModalIsVisible }) => saveChangesModalIsVisible,
);

export const selectShowConfirmationAnimationVisible = createSelector(
  [dealerProfileSettings],
  ({ showConfirmationAnimationVisible }) => showConfirmationAnimationVisible,
);

export const selectSelectedDealerProfileSettingsCategory = createSelector(
  [dealerProfileSettings],
  ({ selectedDealerProfileSettingsCategory }) =>
    selectedDealerProfileSettingsCategory,
);

export const selectNextSelectedDealerId = createSelector(
  [dealerProfileSettings],
  ({ nextSelectedDealerId }) => nextSelectedDealerId,
);
